import React from 'react';
import { MenuItemModel, SubMenuItemModel } from './types.ts';
import SvgMenuRight from '../../app/icons/menu/MenuRight.tsx';
import SubMenuItem from './SubMenuItem.tsx';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import SvgMenuDown from '@app/icons/menu/MenuDown.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { useSecuredDecoration } from '@components/security/securedDecoration';
import MenuBadges, { hasVisibleBadge } from './MenuBadges.tsx';
import MenuAttentionSheen from './MenuAttentionSheen.tsx';

interface ComponentProps extends MenuItemModel {
  /** Index of the active sub-item, or -1. Resolved centrally in Menu so only one entry can win. */
  activeSubIndex?: number;
  /** Called when the user navigates from this item, so the hover overlay can close itself. */
  onNavigate?: () => void;
}

const MenuItem: React.FC<ComponentProps> = ({
  title,
  icon,
  note,
  subMenu,
  elementCount,
  url,
  mode = 'open-inside',
  isOpen = false,
  smallMenu = false,
  active = false,
  activeSubIndex = -1,
  onClick,
  onNavigate,
  dataTestId,
  secured,
  render,
  attention = false,
}) => {
  const decoration = useSecuredDecoration(secured);
  const subItems: SubMenuItemModel[] = Array.isArray(subMenu) ? subMenu : [];
  const hasBadge = hasVisibleBadge(elementCount);

  if (render === false) return null;

  const railBadge = hasBadge && smallMenu && (
    // Sits just outside the icon's top-right corner, so the glyph stays fully readable. Stacked
    // downwards, because a second pill has nowhere to go sideways in an 80px rail.
    <span className={'pointer-events-none absolute -right-2 -top-1.5 z-[1] flex flex-col items-end gap-0.5'}>
      <MenuBadges elementCount={elementCount} compact />
    </span>
  );

  const label = (
    <>
      {icon && (
        // The rail badge is anchored to the icon, not to the row: the row's width, padding and
        // overflow all change with the collapse, and pinning to any of them put the badge over
        // the glyph. Anchored here it sits on the icon's top-right corner wherever the icon is.
        <span className={clsx('relative shrink-0 flex items-center justify-center w-6', { 'text-content-brand': active })}>
          {icon}
          {railBadge}
        </span>
      )}
      {/* The label stays mounted and fades instead of unmounting — unmounting it is what made the
          old collapse animation look like a jump. The panel's overflow-hidden clips it. */}
      <span
        className={twMerge(
          clsx('flex flex-col whitespace-nowrap transition-opacity duration-150 ease-in-out motion-reduce:transition-none', {
            'opacity-0': smallMenu,
            'opacity-100': !smallMenu,
          }),
        )}
        aria-hidden={smallMenu}
      >
        <span>{title}</span>
        {note && <span className={'text-xs text-gray-400'}>{note}</span>}
      </span>
    </>
  );

  const badge = hasBadge && (
    <div className={'flex items-center gap-1'}>
      <MenuBadges elementCount={elementCount} />
    </div>
  );

  /**
   * In the rail the badge cannot ride in the row: the row is only 80px wide, its middle is taken
   * by the icon, and it clips its own overflow to keep the faded-out label from swallowing clicks
   * — which clipped the badge too. So it is rendered as a sibling of the row, pinned to the <li>,
   * and stacked downwards so a second pill grows into empty space rather than off the rail.
   */
  return decoration.decorate(
    <li className={'group relative cursor-pointer select-none'}>
      <div
        onClick={e => onClick?.(e)}
        className={twMerge(
          clsx(
            // Padding rather than justify-center keeps the icon centred in the 80px rail and slides
            // it into place as the panel widens, instead of snapping.
            // overflow-hidden clips the faded-out label to the rail width so it cannot swallow
            // clicks on the page behind it. It sits on the row, not the <li>, so the fly-out
            // sub-menu (a sibling below) is still free to escape the sidebar.
            // `relative` anchors the attention sheen below; the existing overflow-hidden clips it
            // to the row.
            'relative flex items-center justify-between gap-4 h-[48px] pr-4 overflow-hidden transition-[padding,background-color,color] duration-200 ease-in-out motion-reduce:transition-none',
            smallMenu ? 'pl-7' : 'pl-4',
            // Same pair the dropdown/multiselect panels use (see styles.scss `.ringil-filter-panel`):
            // selected is the brand tint, hover is the neutral grey — and hover never repaints a
            // selected row, so selection stays legible while the pointer passes over it.
            active ? 'bg-content-brandMedium text-content-brand' : 'hover:bg-content-quaternary',
          ),
        )}
      >
        {url ? (
          <Link
            to={url}
            onClick={() => onNavigate?.()}
            className={twMerge(clsx('flex gap-4 items-center min-w-0 text-content-primary', { 'text-content-brand': active }))}
            data-testid={dataTestId}
          >
            {label}
          </Link>
        ) : (
          <div className={twMerge(clsx('flex gap-4 items-center min-w-0 text-content-primary', { 'text-content-brand': active }))} data-testid={dataTestId}>
            {label}
          </div>
        )}
        <div
          className={twMerge(
            clsx('flex flex-row items-center gap-2 shrink-0 transition-opacity duration-150 ease-in-out motion-reduce:transition-none', {
              // Nothing in this box belongs in the rail: chevrons make no sense there and the
              // badge is rendered outside the row instead.
              'opacity-0': smallMenu,
            }),
          )}
        >
          {!smallMenu && badge}
          {/* Fixed-width centring box, mirroring the leading icon column. The chevrons are not the
              same size as each other (24 vs 26px) nor as the sub-items' circle-plus (16px), so
              right-aligning them raw put all three on different centres and made the caret jump
              1px on open/close. */}
          {!smallMenu && subMenu && (!Array.isArray(subMenu) || subMenu.length > 0) && (
            <span className={'w-6 shrink-0 flex items-center justify-center'}>
              {mode === 'open-inside' && Array.isArray(subMenu) ? isOpen ? <SvgMenuDown /> : <SvgMenuRight /> : <SvgMenuRight />}
            </span>
          )}
        </div>
        {attention && <MenuAttentionSheen />}
      </div>

      {subMenu && !smallMenu && mode === 'open-inside' && (
        // grid-template-rows 0fr→1fr animates to the natural height without measuring it.
        <div
          className={twMerge(
            clsx('grid transition-[grid-template-rows] duration-200 ease-in-out motion-reduce:transition-none', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'),
          )}
        >
          <ul className={'overflow-hidden min-h-0'}>
            {subItems.map((subItem, index) => (
              <SubMenuItem
                key={index}
                title={subItem.title}
                elementCount={subItem.elementCount}
                onClick={subItem.onClick}
                onNavigate={onNavigate}
                url={subItem.url}
                active={index === activeSubIndex}
                create={subItem.create}
                attention={subItem.attention}
                render={subItem.render}
                content={subItem.content}
                secured={subItem.secured}
                dataTestId={subItem.dataTestId}
              />
            ))}
            {isNotNullOrUndefined(subMenu) && !Array.isArray(subMenu) && <>{subMenu}</>}
          </ul>
        </div>
      )}

      {subMenu && !smallMenu && mode !== 'open-inside' && (
        <ul
          className={twMerge(
            clsx(
              'w-[270px] hidden absolute group-hover:block left-[270px] shadow-md bg-base-tertiary z-[var(--z-menu-flyout)]',
              // The fly-out overlays page content, so it sits above the sticky tab strips
              // (--z-tabs) and below side panels (--z-panel).
              { 'top-0': mode === 'open-outside-top' },
              { 'bottom-0': mode === 'open-outside-bottom' },
            ),
          )}
        >
          {subItems.map((subItem, index) => (
            <SubMenuItem
              key={index}
              title={subItem.title}
              elementCount={subItem.elementCount}
              onClick={subItem.onClick}
              onNavigate={onNavigate}
              url={subItem.url}
              active={index === activeSubIndex}
              create={subItem.create}
              render={subItem.render}
              content={subItem.content}
              secured={subItem.secured}
              dataTestId={subItem.dataTestId}
            />
          ))}
          {isNotNullOrUndefined(subMenu) && !Array.isArray(subMenu) && <>{subMenu}</>}
        </ul>
      )}
    </li>,
  );
};

export default MenuItem;
